import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    OnInit,
    inject,
    input,
    signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';
import {
    ControlContainer,
    FormControl,
    ReactiveFormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AncFieldValidateDirective } from '../../form-validation/anc-field-validate.directive';
import { ancShowFieldError } from '../../form-validation/anc-show-field-error.util';

/**
 * Time field with the Figma outlined floating label (same chrome as
 * lib-anc-form-field).
 *
 * A native `type="time"` input always paints its own `--:--` placeholder, which
 * collides with the resting label. This field renders the input as plain text
 * while it is empty and unfocused, and switches to the native time input once
 * focused or filled — at which point the label has floated out of the way.
 *
 * @example
 * <lib-anc-form-field-for-time [label]="'Time'" [required]="true"
 *   controlName="time_of_day" [ancFieldValidateSubmitted]="submitted"
 *   [requiredErrorMessage]="'Time is required'"></lib-anc-form-field-for-time>
 */
@Component({
    selector: 'lib-anc-form-field-for-time',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        AncFieldValidateDirective
    ],
    templateUrl: './anc-form-field-for-time.component.html',
    styleUrls: ['./anc-form-field-for-time.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncFormFieldForTimeComponent implements OnInit {
    /** Field label text (run through the translate pipe). */
    label = input<string>('');
    /** Renders the required asterisk when true. */
    required = input<boolean>(false);
    /** Reactive-forms control name in the parent `[formGroup]`. */
    controlName = input.required<string>();
    readonly = input<boolean>(false);

    /** When true, invalid controls show error styling/messages even if pristine. */
    validateSubmitted = input(false, { alias: 'ancFieldValidateSubmitted' });
    /** Group-level error for this control (red border without a control-level error). */
    forceInvalid = input(false, { alias: 'ancFieldForceInvalid' });
    /** Built-in required validation message (no projected `[ancError]` needed). */
    requiredErrorMessage = input<string>('');

    /** Parent `[formGroup]` / `formGroupName`. */
    private readonly parentForm = inject(ControlContainer, {
        optional: true,
        skipSelf: true
    });

    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly focused = signal(false);

    ngOnInit(): void {
        // OnPush: re-render on programmatic patches (e.g. edit-mode load) and
        // validation changes, not only on events from this input.
        const control = this.control();
        if (control) {
            merge(control.valueChanges, control.statusChanges)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.cdr.markForCheck());
        }
    }

    protected control(): FormControl | null {
        const control = this.parentForm?.control?.get(this.controlName());
        return control instanceof FormControl ? control : null;
    }

    protected hasValue(): boolean {
        const value = this.control()?.value;
        return value != null && String(value).trim() !== '';
    }

    protected floating(): boolean {
        return this.focused() || this.hasValue();
    }

    /** Native time input only once the label has floated (see class doc). */
    protected inputType(): 'time' | 'text' {
        return this.floating() ? 'time' : 'text';
    }

    protected onFocus(): void {
        this.focused.set(true);
    }

    protected onBlur(): void {
        this.focused.set(false);
    }

    protected showRequiredError(): boolean {
        return (
            !!this.requiredErrorMessage() &&
            ancShowFieldError(this.control(), this.validateSubmitted()) &&
            !!this.control()?.errors?.['required']
        );
    }
}
